import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { LoadingComponent } from "Components";
import { BasicDetails } from "./BasicDetails";
import { PersonalDetails } from "./PersonalDetails";
import { Languages } from "./Languages";
import { Education } from "./Education";
import { Experience } from "./Experience";
import { Publications } from "./Publications";
import { Grants } from "./Grants";
import { Recognitions } from "./Recognitions";
import { OnlineProfiles } from "./OnlineProfiles";
import { AboutMe } from "./AboutMe";
import { ResearchSummary } from "./ResearchSummary";
import { Resume } from "./Resume";
import { Skils } from "./Skils";
import { CareerInterest } from "./CareerInterest";
import { CompletionStatus } from "./CompletionStatus";
import { TrendingJobs } from "./TrendingJobs";
import { newAuthService } from "Service/Axios";
import {
  getUserProfileDetailsByIdUrl,
  getUserProfileDetailsUrl,
} from "Constant/api";

export default function ViewProfile() {
  const { t } = useTranslation();

  const { userId } = useParams();
  const isCurrentUserProfile = userId === null || userId === undefined;

  const [loading, setLoading] = useState(false);
  const [userProfileDetails, setUserProfileDetails] = useState(null);

  const shouldShowSection = (isCurrentUserProfile, data) => {
    if (isCurrentUserProfile) return true;
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    return Object.keys(data).length > 0;
  };

  // Fetch user profile details
  const fetchUserProfileDetails = async (isLoading) => {
    if (isLoading) {
      setLoading(true);
    }
    const payload = {};
    if (userId) {
      payload.userId = userId;
    }
    const response = await newAuthService(
      userId ? getUserProfileDetailsByIdUrl : getUserProfileDetailsUrl,
      "POST",
      payload
    );
    setUserProfileDetails(response.data);
    setLoading(false);
  };

  useEffect(() => {
    fetchUserProfileDetails(true);
  }, [userId]);

  if (!loading && !userProfileDetails)
    return (
      <div className="text-center mt-5">
        <p className="text-gray-500">{t("auth.viewProfile.noProfileData")}</p>
      </div>
    );

  return (
    <LoadingComponent isLoading={loading}>
      <main className="main">
        <div className="row align-items-start">
          <div className="col-xl-9 col-lg-9 col-md-12 col-sm-12 col-12">
            <div className="d-flex flex-column row-gap-3">
              <BasicDetails
                userProfileDetails={userProfileDetails}
                fetchData={fetchUserProfileDetails}
                isCurrentUserProfile={isCurrentUserProfile}
              />

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.aboutme
              ) && (
                <AboutMe
                  aboutMeData={userProfileDetails?.aboutme || ""}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.personalDetails
              ) && (
                <PersonalDetails
                  personalDetailsData={
                    userProfileDetails?.personalDetails || null
                  }
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.languages
              ) && (
                <Languages
                  languageList={userProfileDetails?.languages || []}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.education
              ) && (
                <Education
                  educationList={userProfileDetails?.education || []}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.experience
              ) && (
                <Experience
                  experienceList={userProfileDetails?.experience || []}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.publication
              ) && (
                <Publications
                  publicationList={userProfileDetails?.publication || []}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.grant
              ) && (
                <Grants
                  grantList={userProfileDetails?.grant || []}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.recognition
              ) && (
                <Recognitions
                  recognitionList={userProfileDetails?.recognition || []}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.onlineProfiles
              ) && (
                <OnlineProfiles
                  onlineProfileList={userProfileDetails?.onlineProfiles || []}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.researchSummary
              ) && (
                <ResearchSummary
                  researchSummaryData={
                    userProfileDetails?.researchSummary || ""
                  }
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.resume
              ) && (
                <Resume
                  resumeList={userProfileDetails?.resume || []}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}
            </div>
          </div>
          <div className="col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12 position-sticky top-lg-20">
            <div className="d-flex flex-column justify-content-center row-gap-3">
              {isCurrentUserProfile && <CompletionStatus />}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.skills
              ) && (
                <Skils
                  skillsData={userProfileDetails?.skills || []}
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              {shouldShowSection(
                isCurrentUserProfile,
                userProfileDetails?.careerInterest
              ) && (
                <CareerInterest
                  careerInterestData={
                    userProfileDetails?.careerInterest || null
                  }
                  fetchData={fetchUserProfileDetails}
                  isCurrentUserProfile={isCurrentUserProfile}
                />
              )}

              <TrendingJobs />
            </div>
          </div>
        </div>
      </main>
    </LoadingComponent>
  );
}
